import React, { useState, useRef, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useZustandStore from "../../zustandStore/useZustandStore";
import useGlobalAppAndLanguage from "../../hooks/useGlobalAppAndLanguage";
import { getAgencyIdFromUser } from "../../utils/agencyScope";
import AdsAPIs from "../../apiRoutes/Ads";
import CategoryTrackMappingAPIs from "../../apiRoutes/CategoryTrackMapping";
import { GET_AD_REQUESTS_QUERY_KEY, GET_AD_DELIVERIES_QUERY_KEY, GET_AD_MODERATIONS_QUERY_KEY, GET_CTM_OPTIONS_QUERY_KEY } from "../../utils/constants/queryKeys";
import { ORPHAN_SENTINEL_ID } from "../../utils/constants/ads";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Search, Trash2, Plus } from "lucide-react";
import toast from "react-hot-toast";
import { validateAssetUrl } from "./utils/validateAssetUrl";
import { creatorRequired } from "../AdRequest/utils/directRows";
import { buildDeliveryAdName, isLegacyName, normalizeLegacyAdName } from "../../utils/adNameBuilder";

const initialForm = {
  ad_id: "",
  topic: "",
  app_name: "",
  language: "",
  account: "",
  category: "",
  track: "",
  track_number: "",
  adset_name: "",
  ad_type_id: "",
  creator_name: "",
  editor_name: "",
};

const createEmptyHook = () => ({ hook_name: "", ad_asset_url: "", ad_name: "" });

const HOOKS_COUNT_MIN = 1;
const HOOKS_COUNT_MAX = 10;

/** Requested hook count, clamped — old requests have no value and mean 1. */
function resolveHooksCount(request) {
  const n = Number(request?.hooks_count);
  if (!Number.isInteger(n)) return HOOKS_COUNT_MIN;
  return Math.min(Math.max(n, HOOKS_COUNT_MIN), HOOKS_COUNT_MAX);
}

function hooksForRequest(request, slots = {}) {
  const adSetName = normalizeLegacyAdName(request?.remarks ?? "");
  return Array.from({ length: resolveHooksCount(request) }, (_, i) => ({
    ...createEmptyHook(),
    ad_name: adSetName ? buildDeliveryAdName(adSetName, slots, i + 1) : "",
  }));
}

const ReadOnlyField = ({ label, value, mono = false }) => (
  <div className="min-w-0">
    <p className="text-[11px] text-gray-400">{label}</p>
    <p className={`text-sm text-gray-800 ${mono ? "break-all font-mono text-xs leading-5" : "truncate"}`}>{value || "—"}</p>
  </div>
);

const AdDeliverCreate = ({ onSuccess, resubmitInitial = null, initialRequestId = null }) => {
  const { globalApp, globalLanguage } = useGlobalAppAndLanguage();
  const [hasAdIdAndRequestId, setHasAdIdAndRequestId] = useState(true);
  const [form, setForm] = useState(() => ({
    ...initialForm,
    app_name: globalApp,
    language: globalLanguage,
  }));
  const [hooks, setHooks] = useState([createEmptyHook()]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const currentUser = useZustandStore((s) => s?.currentUser);
  const agencyId = getAgencyIdFromUser(currentUser);

  const { data: pendingResult, isLoading: pendingLoading } = useQuery({
    queryKey: [...GET_AD_REQUESTS_QUERY_KEY, "pending", agencyId],
    queryFn: () => AdsAPIs.listAdRequests({ axiosPrivate, status: "pending", requested_for_agency_id: agencyId || undefined, limit: 100, offset: 0 }),
    enabled: hasAdIdAndRequestId,
  });
  const pendingRequests = pendingResult?.data ?? [];

  const { data: adTypesResult, isLoading: adTypesLoading } = useQuery({
    queryKey: ["adtypes-for-agency", agencyId],
    queryFn: () => AdsAPIs.listAdTypesForAgency({ axiosPrivate, agency_id: agencyId }),
    enabled: !!agencyId && !resubmitInitial,
  });
  const adTypeOptions = adTypesResult?.data ?? [];
  const adTypeOptionsEmpty = !adTypesLoading && adTypeOptions.length === 0;

  // CTM cascade for category/track, sourced for the chosen app (editable, autofilled from request).
  const { data: ctmOptions } = useQuery({
    queryKey: [...GET_CTM_OPTIONS_QUERY_KEY, form.app_name, form.language],
    queryFn: () => CategoryTrackMappingAPIs.options({ axiosPrivate, app_name: form.app_name, language: form.language || undefined }),
    enabled: !!form.app_name,
  });
  const ctmCategories = (ctmOptions?.categories ?? []).filter((c) => c && c.trim());
  const ctmTracksByCategory = ctmOptions?.tracks_by_category ?? {};
  const ctmTracksForCategory = form.category
    ? (ctmTracksByCategory[form.category] ?? []).filter((t) => t && t.track && t.track.trim())
    : [];

  const handleCategoryChange = (v) => {
    if (!v) return;
    setForm((f) => ({ ...f, category: v, track: "", track_number: "" }));
  };
  const handleTrackChange = (v) => {
    if (!v) return;
    const match = (ctmTracksByCategory[form.category] ?? []).find((t) => t.track === v);
    setForm((f) => ({ ...f, track: v, track_number: match && match.track_number != null ? String(match.track_number) : "" }));
  };

  const filtered = searchQuery.trim()
    ? pendingRequests.filter(
        (r) =>
          String(r.ad_id ?? "").includes(searchQuery.trim()) ||
          String(r.id).includes(searchQuery.trim()) ||
          (r.topic && r.topic.toLowerCase().includes(searchQuery.trim().toLowerCase())) ||
          (r.comments && r.comments.toLowerCase().includes(searchQuery.trim().toLowerCase()))
      )
    : pendingRequests;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!selectedRequest) return;
    setForm((f) => ({
      ...f,
      ad_id: selectedRequest.ad_id != null ? String(selectedRequest.ad_id) : "",
      topic: selectedRequest.topic ?? "",
      adset_name:
        (selectedRequest.adset_name || "") ||
        normalizeLegacyAdName(selectedRequest.remarks ?? ""),
      app_name: selectedRequest.for_app_name ?? "",
      language: selectedRequest.language ?? "",
      account: selectedRequest.account ?? "",
      category: selectedRequest.category ?? "",
      track: selectedRequest.track ?? "",
      track_number: selectedRequest.track_number != null ? String(selectedRequest.track_number) : "",
      // The request's ad type is already validated against the agency's rate
      // card, so it is a safe default; the agency can still change it.
      ...(selectedRequest.ad_type_id != null
        ? { ad_type_id: String(selectedRequest.ad_type_id) }
        : {}),
    }));
    // A resubmission targets one existing hook line, so it keeps its single row
    // rather than expanding to the request's full hook count.
    if (resubmitInitial) return;
    // One row per requested hook, ad names pre-filled (agency edits before submit).
    setHooks(hooksForRequest(selectedRequest));
  }, [selectedRequest, resubmitInitial]);

  const isV2Request = !!selectedRequest?.remarks && !isLegacyName(selectedRequest.remarks);
  const needsCreator = creatorRequired(selectedRequest?.human_ai);
  // Delivering against a request: its details are read-only; only link, ad type, creator and editor are ours.
  const requestLocked = hasAdIdAndRequestId && !!selectedRequest;
  const selectedAdTypeName = adTypeOptions.find((t) => String(t.id) === String(form.ad_type_id))?.name || "";
  // Agency slots fill the hook names live; legacy names only carry creator and ad type.
  useEffect(() => {
    if (!selectedRequest?.remarks || resubmitInitial) return;
    const slots = { creator_name: form.creator_name, editor_name: form.editor_name, ad_type: selectedAdTypeName };
    setHooks((prev) =>
      hooksForRequest(selectedRequest, slots).map((h, i) => ({
        ...h,
        ad_asset_url: prev[i]?.ad_asset_url ?? "",
        hook_name: prev[i]?.hook_name ?? "",
      }))
    );
    const baseAdset = normalizeLegacyAdName(selectedRequest.remarks);
    setForm((f) => ({ ...f, adset_name: buildDeliveryAdName(baseAdset, slots) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.creator_name, form.editor_name, selectedAdTypeName, selectedRequest?.remarks]);

  // Fetched by id, not from `pendingRequests`: that list is capped at 100.
  const { data: initialRequest } = useQuery({
    queryKey: [...GET_AD_REQUESTS_QUERY_KEY, "for-delivery", initialRequestId],
    queryFn: () => AdsAPIs.getAdRequest({ axiosPrivate, id: initialRequestId }),
    enabled: !!initialRequestId && !resubmitInitial,
  });

  useEffect(() => {
    if (!initialRequest?.id) return;
    setHasAdIdAndRequestId(true);
    // Same shape the picker sets, so prefill runs through one path.
    setSelectedRequest({
      id: initialRequest.id,
      ad_id: initialRequest.ad_id,
      topic: initialRequest.topic,
      comments: initialRequest.comments,
      adset_name: initialRequest.adset_name,
      for_app_name: initialRequest.for_app_name,
      language: initialRequest.language,
      account: initialRequest.account,
      category: initialRequest.category,
      track: initialRequest.track,
      track_number: initialRequest.track_number,
      remarks: initialRequest.remarks,
      hooks_count: initialRequest.hooks_count,
      ad_type_id: initialRequest.ad_type_id,
    });
    setSearchQuery("");
    setDropdownOpen(false);
  }, [initialRequest]);

  const lockedResubmitRequest = resubmitInitial?.type === "request";
  const lockedResubmitOrphan = resubmitInitial?.type === "orphan";

  useEffect(() => {
    if (!resubmitInitial) return;
    if (resubmitInitial.type === "orphan") {
      setHasAdIdAndRequestId(false);
      setSelectedRequest(null);
      setSearchQuery("");
      setForm((f) => ({
        ...f,
        ad_id: resubmitInitial.ad_id != null ? String(resubmitInitial.ad_id) : "",
        topic: resubmitInitial.topic ?? "",
        adset_name: resubmitInitial.adset_name ?? "",
      }));
      setHooks([{ ...createEmptyHook(), hook_name: resubmitInitial.hook_name || "" }]);
      return;
    }
    setHasAdIdAndRequestId(true);
    setSelectedRequest({
      id: resubmitInitial.ad_request_id,
      ad_id: resubmitInitial.ad_id,
      topic: resubmitInitial.topic ?? "",
      adset_name: resubmitInitial.adset_name ?? "",
    });
    setForm((f) => ({
      ...f,
      ad_id: resubmitInitial.ad_id != null ? String(resubmitInitial.ad_id) : "",
      topic: resubmitInitial.topic ?? "",
      adset_name: resubmitInitial.adset_name ?? "",
    }));
    setHooks([{ ...createEmptyHook(), hook_name: resubmitInitial.hook_name || "" }]);
    setSearchQuery("");
  }, [resubmitInitial]);

  const { mutate: create, isPending } = useMutation({
    mutationFn: (payload) => AdsAPIs.createDelivery({ axiosPrivate, payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GET_AD_DELIVERIES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: GET_AD_REQUESTS_QUERY_KEY });
      if (resubmitInitial?.delivery_id) {
        queryClient.invalidateQueries({ queryKey: GET_AD_MODERATIONS_QUERY_KEY });
      }
      toast.success(resubmitInitial?.delivery_id ? "Resubmission saved." : "Delivery created.");
      setForm({ ...initialForm, app_name: globalApp, language: globalLanguage });
      setHooks([createEmptyHook()]);
      setHasAdIdAndRequestId(true);
      setSelectedRequest(null);
      setSearchQuery("");
      onSuccess?.();
    },
  });

  const updateHook = (idx, key, value) => {
    setHooks((prev) => prev.map((h, i) => (i === idx ? { ...h, [key]: value } : h)));
  };
  const addHook = () => setHooks((prev) => [...prev, createEmptyHook()]);
  const removeHook = (idx) => setHooks((prev) => prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx));

  const updateForm = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    // Number by row position, not by position among the filled rows: filling
    // ads 1 and 3 must deliver hooks 1 and 3, not hooks 1 and 2.
    const validHooks = hooks
      .map((h, idx) => ({ ...h, hook_number: idx + 1 }))
      .filter((h) => (h.ad_asset_url || "").trim());
    if (!validHooks.length) return toast.error("At least one ad with a video URL is required.");
    if (validHooks.some((h) => !validateAssetUrl(h.ad_asset_url).ok)) {
      return toast.error("Fix the video / Drive links before creating (folder links are not allowed).");
    }
    if (!resubmitInitial && !form.ad_type_id) return toast.error("Ad type is required.");
    if (isV2Request && !resubmitInitial) {
      if (needsCreator && !form.creator_name.trim()) return toast.error("Creator name is required for Human ads.");
      if (!form.editor_name.trim()) return toast.error("Editor name is required.");
    }
    let adId, adRequestId;
    if (!hasAdIdAndRequestId) {
      adId = form.ad_id ? parseInt(form.ad_id, 10) : undefined;
      adRequestId = undefined;
      if (adId != null && (Number.isNaN(adId) || adId < 1)) adId = undefined;
    } else {
      if (!selectedRequest) return toast.error("Select an ad request.");
      adRequestId = selectedRequest.id;
      adId = form.ad_id ? parseInt(form.ad_id, 10) : (selectedRequest.ad_id ?? undefined);
      if (adId != null && Number.isNaN(adId)) adId = selectedRequest.ad_id ?? undefined;
    }
    const payload = {
      ad_id: adId,
      ad_request_id: adRequestId,
      hooks: validHooks.map((h) => ({
        ad_asset_url: (h.ad_asset_url || "").trim(),
        ...(requestLocked ? {} : { hook_name: (h.hook_name || "").trim(), ad_name: (h.ad_name || "").trim() || undefined }),
        hook_number: h.hook_number,
        creator_name: (form.creator_name || "").trim() || undefined,
        editor_name: (form.editor_name || "").trim() || undefined,
      })),
      creator_name: (form.creator_name || "").trim() || undefined,
      editor_name: (form.editor_name || "").trim() || undefined,
      ...(requestLocked
        ? {}
        : {
            account: (form.account || "").trim() || undefined,
            category: (form.category || "").trim() || undefined,
            track: (form.track || "").trim() || undefined,
            adset_name: (form.adset_name || "").trim() || undefined,
            topic: (form.topic || "").trim() || undefined,
            for_app_name: (form.app_name || "").trim() || undefined,
            language: (form.language || "").trim() || undefined,
          }),
    };
    if (!requestLocked && form.track_number !== "" && form.track_number != null) payload.track_number = Number(form.track_number);
    if (agencyId) payload.delivered_by_agency_id = Number(agencyId);
    if (!resubmitInitial && form.ad_type_id) {
      payload.ad_type_id = Number(form.ad_type_id);
    }
    if (resubmitInitial?.delivery_id != null && Number(resubmitInitial.delivery_id) > 0) {
      payload.resubmit_delivery_id = Number(resubmitInitial.delivery_id);
    }
    create(payload);
  };

  const hookCountLocked =
    !resubmitInitial && hasAdIdAndRequestId && !!selectedRequest;

  // Only non-empty URLs are submitted; a hook with a bad link (e.g. Drive folder)
  // blocks Create. At least one valid URL is required.
  const nonEmptyHooks = hooks.filter((h) => (h.ad_asset_url || "").trim());
  const hasInvalidAssetUrl = nonEmptyHooks.some((h) => !validateAssetUrl(h.ad_asset_url).ok);
  const canSubmit = nonEmptyHooks.length > 0 && !hasInvalidAssetUrl;

  return (
    <form onSubmit={handleSubmit} className="p-6 max-w-[560px] space-y-4">
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="has-ad-id-request-id"
          checked={hasAdIdAndRequestId}
          disabled={!!resubmitInitial}
          onChange={(e) => {
            const checked = e.target.checked;
            setHasAdIdAndRequestId(checked);
            if (!checked) { setSelectedRequest(null); setSearchQuery(""); setHooks([createEmptyHook()]); }
          }}
          className="h-4 w-4 rounded border-input"
        />
        <Label htmlFor="has-ad-id-request-id" className="font-normal cursor-pointer">
          Deliver for an existing Ad Request ?
        </Label>
      </div>
      {lockedResubmitOrphan && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <p className="font-medium">Resubmit (orphan / manual ad id)</p>
          <p className="text-xs mt-1 text-amber-800">Confirm Ad ID below and paste a new video link. Hook name is prefilled if the rejected delivery had one.</p>
        </div>
      )}
      {hasAdIdAndRequestId && lockedResubmitRequest && selectedRequest && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <p className="font-medium">Resubmit (locked to this ad request)</p>
          <p className="text-xs mt-1 text-amber-800">
            Request #{selectedRequest.id}
            {" · "}
            Ad ID {selectedRequest.ad_id != null && selectedRequest.ad_id !== ORPHAN_SENTINEL_ID ? selectedRequest.ad_id : "—"}
            {selectedRequest.topic ? ` · ${String(selectedRequest.topic).slice(0, 72)}${String(selectedRequest.topic).length > 72 ? "…" : ""}` : ""}
          </p>
        </div>
      )}
      {hasAdIdAndRequestId && !lockedResubmitRequest && (
        <div className="space-y-2 relative" ref={dropdownRef}>
          <Label>Ad request *</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              className="pl-9"
              value={selectedRequest ? `${selectedRequest.ad_id != null && selectedRequest.ad_id !== ORPHAN_SENTINEL_ID ? selectedRequest.ad_id : "—"}${selectedRequest.topic ? ` – ${String(selectedRequest.topic).slice(0, 40)}` : ""}` : searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSelectedRequest(null);
                setDropdownOpen(true);
              }}
              onFocus={() => setDropdownOpen(true)}
              placeholder="Search by ID, topic or comment"
            />
            {selectedRequest && (
              <button
                type="button"
                onClick={() => { setSelectedRequest(null); setSearchQuery(""); setDropdownOpen(true); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-sm"
              >
                Clear
              </button>
            )}
          </div>
          {dropdownOpen && (
            <ul className="absolute z-[1000] w-full mt-1 max-h-48 overflow-auto rounded-md border border-border bg-popover py-1 shadow-md">
              {pendingLoading ? (
                <li className="px-3 py-2 text-sm text-muted-foreground">Loading…</li>
              ) : filtered.length === 0 ? (
                <li className="px-3 py-2 text-sm text-muted-foreground">No pending requests found</li>
              ) : (
                filtered.map((r) => (
                  <li
                    key={r.id}
                    className="px-3 py-2 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground"
                    onClick={() => {
                      setSelectedRequest({ id: r.id, ad_id: r.ad_id, topic: r.topic, comments: r.comments, adset_name: r.adset_name, for_app_name: r.for_app_name, language: r.language, account: r.account, category: r.category, track: r.track, track_number: r.track_number, remarks: r.remarks, hooks_count: r.hooks_count, ad_type_id: r.ad_type_id });
                      setSearchQuery("");
                      setDropdownOpen(false);
                    }}
                  >
                    <span className="font-medium">Ad ID {r.ad_id != null && r.ad_id !== ORPHAN_SENTINEL_ID ? r.ad_id : "—"}</span>
                    {r.topic && <span className="text-muted-foreground ml-1">– {String(r.topic).slice(0, 50)}{r.topic.length > 50 ? "…" : ""}</span>}
                    {r.comments && !r.topic && <span className="text-muted-foreground ml-1">– {String(r.comments).slice(0, 50)}{r.comments.length > 50 ? "…" : ""}</span>}
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
      )}
      {requestLocked ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2.5">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">From the ad request</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            <ReadOnlyField label="Topic" value={form.topic} />
            <ReadOnlyField label="App name" value={form.app_name} />
            <ReadOnlyField label="Account" value={form.account} />
            <ReadOnlyField label="Category" value={form.category} />
            <ReadOnlyField label="Track" value={form.track} />
            <ReadOnlyField label="Track number" value={form.track_number} />
          </div>
          <ReadOnlyField label="Ad set name" value={form.adset_name} mono />
        </div>
      ) : (
        <>
      <div className="space-y-2">
        <Label>Topic</Label>
        <Input value={form.topic} onChange={(e) => updateForm("topic", e.target.value)} placeholder="Topic" maxLength={255} />
      </div>
      <div className="space-y-2">
        <Label>App name</Label>
        <Input value={form.app_name} onChange={(e) => updateForm("app_name", e.target.value)} placeholder="App name" maxLength={64} />
      </div>
      <div className="space-y-2">
        <Label>Account</Label>
        <Input value={form.account} onChange={(e) => updateForm("account", e.target.value)} placeholder="Account" maxLength={255} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Category</Label>
          <Select value={form.category || ""} onValueChange={handleCategoryChange} disabled={!form.app_name}>
            <SelectTrigger>
              <SelectValue placeholder={form.app_name ? "Select category" : "Set app name first"} />
            </SelectTrigger>
            <SelectContent>
              {ctmCategories.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Track</Label>
          <Select value={form.track || ""} onValueChange={handleTrackChange} disabled={!form.category}>
            <SelectTrigger>
              <SelectValue placeholder={form.category ? "Select track" : "Select category first"} />
            </SelectTrigger>
            <SelectContent>
              {ctmTracksForCategory.map((t) => (
                <SelectItem key={t.track} value={t.track}>
                  {t.track}{t.track_number != null ? ` (#${t.track_number})` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
        <Label>Track number</Label>
        <Input value={form.track_number} disabled placeholder="Auto from track" />
      </div>
      <div className="space-y-2">
        <Label>Ad Set Name</Label>
        <Input value={form.adset_name} onChange={(e) => updateForm("adset_name", e.target.value)} placeholder="Ad Set Name" maxLength={400} />
      </div>
        </>
      )}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>
            Ads *
            {hookCountLocked && (
              <span className="ml-1.5 font-normal text-xs text-muted-foreground">
                {hooks.length} hook{hooks.length > 1 ? "s" : ""} requested
              </span>
            )}
          </Label>
          {!hookCountLocked && (
            <Button type="button" variant="outline" size="sm" onClick={addHook} className="h-7 text-xs gap-1">
              <Plus className="w-3 h-3" /> Add ad
            </Button>
          )}
        </div>
        {hooks.map((hook, idx) => (
          <div key={idx} className="flex gap-2 items-start p-3 rounded-lg border border-gray-200 bg-gray-50/50">
            <div className="flex-1 space-y-2">
              {hookCountLocked && (
                <p className="text-xs font-medium text-gray-500">Hook {idx + 1}</p>
              )}
              {requestLocked ? (
                <p className="break-all rounded-md border border-gray-200 bg-white px-3 py-1.5 font-mono text-xs text-gray-600">
                  {hook.ad_name || "—"}
                </p>
              ) : (
                <textarea
                  value={hook.ad_name}
                  onChange={(e) => updateHook(idx, "ad_name", e.target.value)}
                  placeholder="Ad name"
                  rows={2}
                  maxLength={512}
                  className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-xs font-mono resize-y focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
              )}
              <Input
                value={hook.ad_asset_url}
                onChange={(e) => updateHook(idx, "ad_asset_url", e.target.value)}
                placeholder="Video URL or Drive link"
                aria-invalid={!validateAssetUrl(hook.ad_asset_url).ok && !!(hook.ad_asset_url || "").trim()}
              />
              {(hook.ad_asset_url || "").trim() && validateAssetUrl(hook.ad_asset_url).error && (
                <p className="text-xs text-red-600">{validateAssetUrl(hook.ad_asset_url).error}</p>
              )}
            </div>
            {!hookCountLocked && hooks.length > 1 && (
              <button type="button" onClick={() => removeHook(idx)} className="mt-1 p-1.5 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>
      {!resubmitInitial && (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>Creator name{isV2Request && needsCreator ? " *" : ""}</Label>
            <Input value={form.creator_name} onChange={(e) => updateForm("creator_name", e.target.value)} placeholder="Who made the ad" maxLength={128} />
          </div>
          <div className="space-y-2">
            <Label>Editor name{isV2Request ? " *" : ""}</Label>
            <Input value={form.editor_name} onChange={(e) => updateForm("editor_name", e.target.value)} placeholder="Who edited it" maxLength={128} />
          </div>
        </div>
      )}
      {!resubmitInitial && (
        <div className="space-y-2">
          <Label>Ad type *</Label>
          <Select
            value={form.ad_type_id ? String(form.ad_type_id) : ""}
            onValueChange={(v) => updateForm("ad_type_id", v)}
            disabled={!agencyId || adTypesLoading || adTypeOptionsEmpty}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={
                  !agencyId
                    ? "Agency required"
                    : adTypesLoading
                    ? "Loading…"
                    : adTypeOptionsEmpty
                    ? "No ad types in this agency's rate card"
                    : "Select ad type"
                }
              />
            </SelectTrigger>
            <SelectContent>
              {adTypeOptions.map((t) => (
                <SelectItem key={t.id} value={String(t.id)}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {adTypeOptionsEmpty && (
            <p className="text-xs text-muted-foreground">
              No ad types configured for this agency. Add rate cards first.
            </p>
          )}
        </div>
      )}
      <Button type="submit" disabled={isPending || !canSubmit}>
        {isPending ? "Creating…" : `Create delivery (${nonEmptyHooks.length} ad${nonEmptyHooks.length !== 1 ? "s" : ""})`}
      </Button>
    </form>
  );
};

export default AdDeliverCreate;
